"use client"

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { cn, buildRegionUrl } from '../lib/utils'
import { useRegion } from '../hooks/use-region'
import { useRegionOptions } from '../hooks/use-region-options'
import { useSession } from '@/features/auth/context/session.context'
import { updateSelectedRegion, getProfile } from '@/features/profile/api/profile.api'
import { useQueryClient } from '@tanstack/react-query'

export interface LocationOption {
  id: string
  label: string
  slug: string
}

interface LocationSelectorProps {
  options?: LocationOption[]
  defaultLocation?: string
  onLocationChange?: (location: string) => void
  className?: string
}

function LocationSelector({
  options: providedOptions,
  defaultLocation,
  onLocationChange,
  className,
}: LocationSelectorProps) {
  const { profile, setProfile, isLoading: sessionLoading, session } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { regionSlug } = useRegion()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const profileSelectedRegionId = profile?.selectedRegion
    ? profile.selectedRegion.toString()
    : null

  // Wait for session so /regions/options is not fired without a JWT (race that caused blank dropdown).
  const canFetchRegions = !sessionLoading && !!session && !providedOptions
  const {
    options: queriedOptions,
    isLoading: regionsLoading,
    isError: regionsError,
    refetch: refetchRegions,
  } = useRegionOptions(false, { enabled: canFetchRegions })

  const options: LocationOption[] = useMemo(() => {
    if (providedOptions) return providedOptions
    return queriedOptions.map((o) => ({
      id: o.value,
      label: o.label,
      slug: o.slug,
    }))
  }, [providedOptions, queriedOptions])

  // True until we have options, hit an error, or were given options as props.
  // Includes session wait so we never fire/finish the request before JWT exists.
  const loading = !providedOptions && (sessionLoading || (canFetchRegions && regionsLoading))

  // Close the menu if options disappear (e.g. error / refetch) so we never show an empty panel
  useEffect(() => {
    if (isOpen && (options.length === 0 || regionsError)) {
      setIsOpen(false)
    }
  }, [isOpen, options.length, regionsError])

  // Initialize selectedLocation from URL, explicit default, or profile preference
  useEffect(() => {
    if (loading || sessionLoading || options.length === 0) return

    if (regionSlug) {
      const regionFromUrl = options.find((opt) => opt.slug === regionSlug)
      if (regionFromUrl) {
        setSelectedLocation(regionFromUrl.id)
        return
      }
    }

    if (defaultLocation) {
      const region = options.find((opt) => opt.id === defaultLocation)
      if (region) {
        setSelectedLocation(region.id)
        return
      }
    }

    if (profileSelectedRegionId) {
      const region = options.find((opt) => opt.id === profileSelectedRegionId)
      if (region) {
        setSelectedLocation(region.id)
        return
      }
    }

    if (!selectedLocation && options.length > 0) {
      setSelectedLocation(options[0].id)
    }
  }, [loading, sessionLoading, options, defaultLocation, profileSelectedRegionId, regionSlug, selectedLocation])

  const selectedOption = options.find((option) => option.id === selectedLocation) || options[0]

  // Notify parent of initial selection
  useEffect(() => {
    if (selectedLocation && !loading && !sessionLoading) {
      onLocationChange?.(selectedLocation)
    }
  }, [selectedLocation, loading, sessionLoading, onLocationChange])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLocationSelect = async (locationId: string) => {
    const selectedRegion = options.find((opt) => opt.id === locationId)
    if (!selectedRegion) return

    const previousSelection = selectedLocation

    // Optimistically update UI
    setSelectedLocation(locationId)
    setIsOpen(false)

    const currentRegionSlug = regionSlug
    const isRegionRoute = currentRegionSlug && pathname?.startsWith(`/${currentRegionSlug}`)

    if (isRegionRoute && selectedRegion.slug) {
      const pathAfterRegion = pathname.replace(`/${currentRegionSlug}`, '') || '/'
      const queryString = searchParams.toString()
      const newPath = `/${selectedRegion.slug}${pathAfterRegion}${queryString ? `?${queryString}` : ''}`

      router.replace(newPath)

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['homes'] })
        queryClient.invalidateQueries({ queryKey: ['homeowners'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['work-orders'] })
        queryClient.invalidateQueries({ queryKey: ['regions'] })
        queryClient.invalidateQueries({ queryKey: ['priority'] })
        router.refresh()
      }, 100)
    } else if (selectedRegion.slug) {
      const newPath = buildRegionUrl(selectedRegion.slug, '/home')

      router.replace(newPath)

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['homes'] })
        queryClient.invalidateQueries({ queryKey: ['homeowners'] })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        queryClient.invalidateQueries({ queryKey: ['work-orders'] })
        queryClient.invalidateQueries({ queryKey: ['regions'] })
        queryClient.invalidateQueries({ queryKey: ['priority'] })
        router.refresh()
      }, 100)
    }

    try {
      setSaving(true)
      const result = await updateSelectedRegion(locationId)

      if (result.success && result.data) {
        setProfile(result.data)
        try {
          const freshProfile = await getProfile()
          if (freshProfile.success && freshProfile.data) {
            setProfile(freshProfile.data)
          }
        } catch (profileError) {
          console.warn('Failed to refresh profile after region update:', profileError)
        }
      } else if (!result.success) {
        console.error('Failed to update selected region:', result.message)
        if (previousSelection) {
          const previousRegion = options.find((opt) => opt.id === previousSelection)
          if (previousRegion) {
            setSelectedLocation(previousSelection)
            onLocationChange?.(previousSelection)
          }
        }
      }
    } catch (error) {
      console.error('Error updating selected region:', error)
      if (previousSelection) {
        const previousRegion = options.find((opt) => opt.id === previousSelection)
        if (previousRegion) {
          setSelectedLocation(previousSelection)
          onLocationChange?.(previousSelection)
        }
      }
    } finally {
      setSaving(false)
    }
  }

  const labelFromSlug = regionSlug
    ? regionSlug
        .split('-')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : null

  const displayLabel = selectedOption?.label ?? labelFromSlug ?? 'Loading...'
  const canOpenMenu = !loading && !regionsError && options.length > 0
  const isDisabled = saving || loading || (!canOpenMenu && !regionsError)

  const showLoadingState = loading && !labelFromSlug && !regionsError

  if (showLoadingState) {
    return (
      <div className={cn('relative', className)}>
        <button
          disabled
          className="flex items-center gap-2 opacity-50"
          aria-expanded={false}
        >
          <h2 className="text-xl md:text-2xl font-semibold leading-none">
            Loading...
          </h2>
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>
    )
  }

  const handleToggle = () => {
    if (regionsError || options.length === 0) {
      void refetchRegions()
      return
    }
    if (!canOpenMenu) return
    setIsOpen((open) => !open)
  }

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        onClick={handleToggle}
        disabled={isDisabled && !regionsError}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-50"
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={
          regionsError
            ? 'Could not load regions. Click to retry.'
            : options.length === 0 && !loading
              ? 'No regions available. Click to retry.'
              : undefined
        }
      >
        <h2 className="text-xl md:text-2xl font-semibold leading-none">
          {displayLabel}
        </h2>
        <ChevronDown className={cn('w-5 h-5 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && canOpenMenu && (
        <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleLocationSelect(option.id)}
                className={cn(
                  'w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors',
                  selectedLocation === option.id && 'bg-gray-100 font-medium',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {regionsError && !isOpen && (
        <p className="absolute top-full right-0 mt-1 text-xs text-red-600 whitespace-nowrap">
          Couldn&apos;t load regions. Click to retry.
        </p>
      )}
    </div>
  )
}

export default LocationSelector
